import { Suspense, useRef, useState } from "react";
import { lazyWithReload } from "../utils/lazyWithReload";
import { Outlet } from "react-router-dom";
import StatusRegistrySync from "./StatusRegistrySync";
import Header from "./Header";
import ClockedInBanner from "./ClockedInBanner";
import ViewAsBanner from "./ViewAsBanner";
import UploadProgressWidget from "./UploadProgressWidget";
import Toaster from "./Toaster";
import ConfirmHost from "./ConfirmHost";
import RouteFallback from "./RouteFallback";
import PullToRefresh from "./PullToRefresh";
import { AnnouncementSplash } from "./Announcements";

// ⚠️ Lazy, but mounted in the SHELL rather than under a route. Lazy keeps the
// seven calculators out of the main bundle until someone first opens them;
// living here (not in <main>) is what lets the window stay open, keep its
// position and keep every typed value while the user navigates between pages.
const PmoToolsWindow = lazyWithReload(() => import("./tools/PmoToolsWindow"));

export default function Layout() {
  // Owned here, not in Header: the button lives in the header but the window
  // must outlive any single page, and both need the same piece of state.
  const [pmoToolsOpen, setPmoToolsOpen] = useState(false);
  // Latches on first open and never clears, so the window stays MOUNTED (just
  // hidden) once used. Unmounting it on close would throw away every value
  // typed into a calculator, which is the one thing this window must not do.
  const [pmoToolsUsed, setPmoToolsUsed] = useState(false);
  const mainRef = useRef(null);

  const togglePmoTools = () => {
    setPmoToolsUsed(true);
    setPmoToolsOpen((o) => !o);
  };

  return (
    <div className="flex flex-col h-dvh bg-surface-muted text-ink antialiased">
      <Header
        pmoToolsOpen={pmoToolsOpen}
        onTogglePmoTools={togglePmoTools}
      />
      {/* Sits between the header and the scrolling content, so a running
          clock stays visible on every page at every width. Renders nothing
          when the user has no open punch. */}
      <ClockedInBanner />
      {/* Same placement — renders nothing unless "View As" is active. */}
      <ViewAsBanner />
      {/* Outside <main>: it slides <main> down, and must not move with it. */}
      <PullToRefresh scrollerRef={mainRef} />
      <main ref={mainRef} className="flex-1 min-h-0 overflow-y-auto overscroll-y-contain">
        {/* Every route below Layout is lazily loaded (App.jsx), so the chunk
            fetch needs a boundary. It sits HERE rather than around <Routes>
            so the header and nav stay mounted while a page loads — wrapping
            the router instead would blank the whole shell on every first
            visit to a route. */}
        <Suspense fallback={<RouteFallback />}>
          <Outlet />
          {/* Keeps the status registry (every type's status flow) current for the
              lists, filters, charts and progress bars that span types. */}
          <StatusRegistrySync />
        </Suspense>
      </main>
      {pmoToolsUsed && (
        <Suspense fallback={null}>
          <PmoToolsWindow open={pmoToolsOpen} onClose={() => setPmoToolsOpen(false)} />
        </Suspense>
      )}

      <UploadProgressWidget />
      <Toaster />
      <ConfirmHost />
      <AnnouncementSplash />
    </div>
  );
}
